import React, { useState, useEffect, useCallback } from 'react';
import { 
  BookOpen, ThumbsUp, Share2, MessageSquare, Send, Filter,
  Search, Building2, Clock, Eye, ChevronDown, Bell, X,
  CheckCircle, AlertCircle, ExternalLink, FileText, Heart, Lightbulb,
  Plus, Users, Globe, Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============== MAIN COMPONENT ==============
const BestPracticesHub = () => {
  const [practices, setPractices] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [filterPillar, setFilterPillar] = useState('all');
  const [filterSize, setFilterSize] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Get user info from localStorage
  const organizationId = localStorage.getItem('cness_organization_id') || '';
  const organizationName = localStorage.getItem('cness_organization_name') || '';
  const userRole = localStorage.getItem('cness_user_role') || '';
  
  // Check if user is certified (can submit best practices)
  const isCertified = localStorage.getItem('cness_certification_status') === 'certified';

  const fetchPractices = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/best-practices`);
      const data = await res.json();
      setPractices(data.practices || []);
    } catch (err) {
      console.error('Failed to fetch practices:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMySubmissions = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/org/best-practices/submissions/${organizationId}`);
      const data = await res.json();
      setMySubmissions(data.submissions || []);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    }
  }, [organizationId]);

  const fetchNotifications = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/org/notifications/${organizationId}`);
      const data = await res.json();
      setNotifications(data.notifications || []);
      
      const unreadRes = await fetch(`${BACKEND_URL}/api/org/notifications/${organizationId}/unread-count`);
      const unreadData = await unreadRes.json();
      setUnreadCount(unreadData.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchPractices();
    fetchMySubmissions();
    fetchNotifications();
  }, [fetchPractices, fetchMySubmissions, fetchNotifications]);

  const handleLike = async (practiceId) => {
    try {
      await fetch(`${BACKEND_URL}/api/best-practices/${practiceId}/like?organization_id=${organizationId}`, {
        method: 'POST'
      });
      fetchPractices();
    } catch (err) {
      console.error('Failed to like:', err);
    }
  };

  const handleShare = async (practiceId) => {
    try {
      await fetch(`${BACKEND_URL}/api/best-practices/${practiceId}/share?organization_id=${organizationId}`, {
        method: 'POST'
      });
      navigator.clipboard.writeText(`${window.location.origin}/best-practices/${practiceId}`);
      alert('Link copied to clipboard!');
      fetchPractices();
    } catch (err) {
      console.error('Failed to share:', err);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/org/notifications/${notificationId}/read`, { method: 'PUT' });
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark read:', err);
    }
  };

  const uniquePillars = [...new Set(practices.map(p => p.pillar).filter(Boolean))];

  const filteredPractices = practices
    .filter(p => filterPillar === 'all' || p.pillar === filterPillar)
    .filter(p => filterSize === 'all' || p.organization_size?.includes(filterSize))
    .filter(p => 
      p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
            <Lightbulb className="w-7 h-7 text-amber-500" />
            Best Practices Hub
          </h1>
          <p className="text-gray-600 text-sm">Learn from certified organizations worldwide</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Notification Bell */}
          {organizationId && (
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                data-testid="notifications-btn"
              >
                <Bell className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-bold text-gray-900">Notifications</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No notifications</div>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          onClick={() => markNotificationRead(n.id)}
                          className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${!n.read ? 'bg-blue-50' : ''}`}
                        >
                          <p className="font-semibold text-gray-900 text-sm">{n.title}</p>
                          <p className="text-gray-600 text-xs mt-1">{n.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submit Best Practice Button (Certified Only) */}
          {isCertified && (
            <button
              onClick={() => setShowSubmitModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-all"
              data-testid="submit-practice-btn"
            >
              <Plus className="w-5 h-5" />
              Submit Best Practice
            </button>
          )}
        </div>
      </div>

      {/* Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-indigo-900">Share Best Practices</h3>
          </div>
          <p className="text-sm text-indigo-800">
            {isCertified 
              ? 'Submit your innovative practices to help other organizations grow. Build your reputation as a thought leader.'
              : 'Get certified to share your best practices with the community and gain recognition.'}
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-emerald-900">Learn from Leaders</h3>
          </div>
          <p className="text-sm text-emerald-800">
            Browse proven practices from certified organizations. Implement strategies that have delivered real results.
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-amber-900">Build Trust</h3>
          </div>
          <p className="text-sm text-amber-800">
            Certified practices are reviewed and verified. Trust that what you're implementing has been proven effective.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('browse')}
          className={`pb-3 px-2 font-semibold border-b-2 transition-all ${
            activeTab === 'browse' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Browse Practices
        </button>
        {organizationId && (
          <button
            onClick={() => setActiveTab('my-submissions')}
            className={`pb-3 px-2 font-semibold border-b-2 transition-all ${
              activeTab === 'my-submissions' 
                ? 'border-indigo-600 text-indigo-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Submissions ({mySubmissions.length})
          </button>
        )}
      </div>

      {activeTab === 'browse' && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search best practices..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <select
              value={filterPillar}
              onChange={(e) => setFilterPillar(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Pillars</option>
              {uniquePillars.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            <select
              value={filterSize}
              onChange={(e) => setFilterSize(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Sizes</option>
              <option value="MICRO">Micro</option>
              <option value="SMALL">Small</option>
              <option value="MEDIUM">Medium</option>
              <option value="LARGE">Large</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
          </div>

          {/* Empty State */}
          {filteredPractices.length === 0 && !loading && (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">No Best Practices Available</h3>
              <p className="text-gray-500">
                Best practices will appear here once published by the admin.
              </p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="text-center py-16">
              <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading best practices...</p>
            </div>
          )}

          {/* Practices Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPractices.map(practice => (
              <PracticeCard
                key={practice.id}
                practice={practice}
                organizationId={organizationId}
                onView={() => setSelectedPractice(practice)}
                onLike={() => handleLike(practice.id)}
                onShare={() => handleShare(practice.id)}
              />
            ))}
          </div>
        </>
      )}

      {/* My Submissions Tab */}
      {activeTab === 'my-submissions' && (
        <div>
          {mySubmissions.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">No Submissions Yet</h3>
              <p className="text-gray-500 mb-6">
                {isCertified 
                  ? "Share your organization's best practices with the community." 
                  : "Complete certification to submit best practices."}
              </p>
              {isCertified && (
                <button
                  onClick={() => setShowSubmitModal(true)}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700"
                >
                  Submit Your First Practice
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {mySubmissions.map(submission => (
                <SubmissionCard key={submission.id} submission={submission} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Practice Detail Modal */}
      {selectedPractice && (
        <PracticeDetailModal
          practice={selectedPractice}
          organizationId={organizationId}
          organizationName={organizationName}
          onClose={() => setSelectedPractice(null)}
          onRefresh={fetchPractices}
        />
      )}

      {/* Submit Best Practice Modal */}
      {showSubmitModal && (
        <SubmitPracticeModal
          organizationId={organizationId}
          organizationName={organizationName}
          onClose={() => setShowSubmitModal(false)}
          onSuccess={() => { setShowSubmitModal(false); fetchMySubmissions(); }}
        />
      )}
    </div>
  );
};


// ============== PRACTICE CARD ==============
const PracticeCard = ({ practice, organizationId, onView, onLike, onShare }) => {
  const isLiked = practice.liked_by?.includes(organizationId);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group">
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
            {practice.pillar}
          </span>
          {practice.type === 'org_submitted' && (
            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
              Community
            </span>
          )}
        </div>
        
        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
          {practice.title}
        </h3>
        
        <p className="text-gray-600 text-sm line-clamp-3">{practice.description}</p>
      </div>

      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {practice.metrics?.views || 0}
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-4 h-4" />
              {practice.metrics?.likes || 0}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <Building2 className="w-4 h-4" />
            {Array.isArray(practice.organization_size) 
              ? practice.organization_size.join(', ')
              : practice.organization_size || 'All'}
          </span>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={onLike}
            className={`p-2 rounded-lg transition-all ${
              isLiked ? 'bg-red-100 text-red-600' : 'hover:bg-gray-100 text-gray-500'
            }`}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
          </button>
          <button onClick={onShare} className="p-2 hover:bg-gray-100 text-gray-500 rounded-lg transition-all">
            <Share2 className="w-5 h-5" />
          </button>
        </div>
        
        <button
          onClick={onView}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-all"
        >
          View Details
        </button>
      </div>
    </div>
  );
};


// ============== SUBMISSION CARD ==============
const SubmissionCard = ({ submission }) => {
  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    published: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-bold text-gray-900">{submission.title}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[submission.status] || 'bg-gray-100 text-gray-700'}`}>
              {submission.status}
            </span>
          </div>
          <p className="text-gray-600 text-sm mb-2">{submission.pillar} / {submission.sub_pillar}</p>
          <p className="text-gray-500 text-sm line-clamp-2">{submission.description}</p>
        </div>
      </div>
      
      {submission.status === 'rejected' && submission.rejection_reason && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-800 text-sm font-semibold">Rejection Reason:</p>
          <p className="text-red-700 text-sm">{submission.rejection_reason}</p>
        </div>
      )}
    </div>
  );
};


// ============== PRACTICE DETAIL MODAL ==============
const PracticeDetailModal = ({ practice, organizationId, organizationName, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [questionText, setQuestionText] = useState('');
  const [remarkText, setRemarkText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) return;
    setSubmitting(true);
    
    try {
      await fetch(`${BACKEND_URL}/api/best-practices/${practice.id}/question?organization_id=${organizationId}&organization_name=${encodeURIComponent(organizationName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ best_practice_id: practice.id, question_text: questionText })
      });
      setQuestionText('');
      alert('Question submitted! Admin will respond soon.');
      onRefresh();
    } catch (err) {
      console.error('Failed to submit question:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRemark = async () => {
    if (!remarkText.trim()) return;
    setSubmitting(true);
    
    try {
      await fetch(`${BACKEND_URL}/api/best-practices/${practice.id}/remark?organization_id=${organizationId}&organization_name=${encodeURIComponent(organizationName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ best_practice_id: practice.id, remark_text: remarkText })
      });
      setRemarkText('');
      alert('Feedback submitted!');
      onRefresh();
    } catch (err) {
      console.error('Failed to submit remark:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-start justify-between">
            <div>
              <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-semibold mb-2 inline-block">
                {practice.pillar}
              </span>
              <h2 className="text-2xl font-black">{practice.title}</h2>
              <p className="text-indigo-100 mt-1">{practice.sub_pillar}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex items-center gap-6 mt-4 text-indigo-100">
            <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {practice.metrics?.views || 0} views</span>
            <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {practice.metrics?.likes || 0} likes</span>
            <span className="flex items-center gap-1"><Share2 className="w-4 h-4" /> {practice.metrics?.shares || 0} shares</span>
          </div>
        </div>

        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-6">
            {[
              { id: 'details', label: 'Details' },
              { id: 'questions', label: 'Ask Question' },
              { id: 'remarks', label: 'Leave Feedback' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 font-semibold border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700">{practice.description}</p>
              </div>

              {practice.implementation_steps?.length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Implementation Steps</h3>
                  <ol className="list-decimal list-inside space-y-2">
                    {practice.implementation_steps.map((step, idx) => (
                      <li key={idx} className="text-gray-700">{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {practice.expected_outcomes?.length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Expected Outcomes</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {practice.expected_outcomes.map((outcome, idx) => (
                      <li key={idx} className="text-gray-700">{outcome}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">Organization Sizes</p>
                  <p className="font-semibold">
                    {Array.isArray(practice.organization_size) 
                      ? practice.organization_size.join(', ')
                      : practice.organization_size || 'All'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">Timeframe</p>
                  <p className="font-semibold">{practice.timeframe || 'Not specified'}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'questions' && organizationId && (
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Ask a Question</h3>
              <p className="text-gray-600 text-sm mb-4">
                Have a question about this best practice? Ask here and the admin will respond.
              </p>
              <textarea
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Type your question..."
              />
              <button
                onClick={handleSubmitQuestion}
                disabled={submitting || !questionText.trim()}
                className="mt-4 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Send className="w-5 h-5" />
                {submitting ? 'Submitting...' : 'Submit Question'}
              </button>
            </div>
          )}

          {activeTab === 'remarks' && organizationId && (
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Leave Feedback</h3>
              <p className="text-gray-600 text-sm mb-4">
                Share your thoughts or experiences with this best practice.
              </p>
              <textarea
                value={remarkText}
                onChange={(e) => setRemarkText(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Share your feedback..."
              />
              <button
                onClick={handleSubmitRemark}
                disabled={submitting || !remarkText.trim()}
                className="mt-4 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <MessageSquare className="w-5 h-5" />
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          )}

          {(activeTab === 'questions' || activeTab === 'remarks') && !organizationId && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Please log in to interact with best practices.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============== SUBMIT PRACTICE MODAL ==============
const SubmitPracticeModal = ({ organizationId, organizationName, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pillar: 'CONSCIOUS LEADERSHIP',
    sub_pillar: 'Purpose-Driven Leadership',
    implementation_steps: [''],
    expected_outcomes: [''],
    evidence: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const PILLARS = [
    { name: "CONSCIOUS LEADERSHIP", sub_pillars: ["Purpose-Driven Leadership", "Transparency & Trust", "Ethical Decision-Making"] },
    { name: "NURTURING CULTURE", sub_pillars: ["Team Collaboration", "Fair Treatment", "Learning & Growth"] },
    { name: "ELEVATED SOLUTIONS", sub_pillars: ["Product Quality", "Accessibility", "Customer-Centric Innovation"] },
    { name: "STAKEHOLDER SYNERGY", sub_pillars: ["Customer Retention", "Customer Relationships", "Customer Feedback"] },
    { name: "SOCIETAL STEWARDSHIP", sub_pillars: ["Community Impact", "Responsible Business", "Positive Impact"] }
  ];

  const selectedPillar = PILLARS.find(p => p.name === formData.pillar);

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      setError('Title and description are required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        ...formData,
        implementation_steps: formData.implementation_steps.filter(s => s.trim()),
        expected_outcomes: formData.expected_outcomes.filter(o => o.trim())
      };

      const res = await fetch(`${BACKEND_URL}/api/org/best-practices/submit?organization_id=${organizationId}&organization_name=${encodeURIComponent(organizationName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        onSuccess();
        alert('Best practice submitted for approval!');
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to submit');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 rounded-t-2xl">
          <h2 className="text-2xl font-black">Submit Your Best Practice</h2>
          <p className="text-green-100 mt-1">Share your success with the community</p>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter best practice title..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Describe this best practice..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Pillar</label>
              <select
                value={formData.pillar}
                onChange={(e) => {
                  const pillar = PILLARS.find(p => p.name === e.target.value);
                  setFormData({ ...formData, pillar: e.target.value, sub_pillar: pillar?.sub_pillars[0] || '' });
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {PILLARS.map(p => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sub-Pillar</label>
              <select
                value={formData.sub_pillar}
                onChange={(e) => setFormData({ ...formData, sub_pillar: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {selectedPillar?.sub_pillars.map(sp => (
                  <option key={sp} value={sp}>{sp}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-2xl border-t border-gray-200 flex gap-3">
          <button onClick={onClose} className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting} className="flex-1 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50">
            {submitting ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BestPracticesHub;
